// src/pages/ModuloRecursos.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useCourseStructure } from "../context/CourseStructureContext";
import {
  FolderOpen,
  FileText,
  Video,
  Link as LinkIcon,
  ArrowLeft,
  Eye,
  Download,
} from "lucide-react";

/* ===================== Helpers ===================== */
const FILE_ICONS = {
  pdf: FileText,
  "application/pdf": FileText,
  video: Video,
  "video/mp4": Video,
  "video/avi": Video,
  "video/mov": Video,
  "video/wmv": Video,
  link: LinkIcon,
  ppt: FileText, // usaremos el mismo ícono pero con color naranja
};

const fileColor = (t = "") => {
  t = String(t).toLowerCase();
  if (t.includes("pdf")) return "#dc2626";
  if (t.includes("ppt")) return "#ea580c";
  if (t.includes("video") || ["mp4", "avi", "mov", "wmv"].some((x) => t.includes(x))) return "#2563eb";
  if (t.includes("link")) return "#065f46";
  return "#6b7280";
};

// === PowerPoint helpers ===
const isPptMime = (mime = "") =>
  /vnd\.ms-powerpoint|vnd\.openxmlformats-officedocument\.presentationml/i.test(String(mime));

const isPptExt = (s = "") => /\.(pptx?|pptm)(\?|#|$)/i.test(String(s));

const isPowerPoint = (r) => {
  const mime = (r?.mime || r?.content_type || "").toLowerCase();
  const ext = (r?.extension || "").toLowerCase();
  const url = String(r?.url || r?.enlace || r?.link || r?.file_name || r?.nombre_archivo || "");
  return isPptMime(mime) || ext === "ppt" || ext === "pptx" || isPptExt(url);
};

const fmtBytes = (bytes = 0) => {
  if (!bytes) return "—";
  const k = 1024,
    sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const safeDate = (d) => {
  try {
    const v = d ? new Date(d) : null;
    return v && !isNaN(v) ? v.toLocaleDateString() : "";
  } catch {
    return "";
  }
};

const pick = (obj, keys, d = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).length) return v;
  }
  return d;
};

const unwrapUrl = (u) => {
  if (!u) return "";
  if (typeof u === "string") return u;
  return u.url || u.signedUrl || u.signed_url || u.link || u.href || u.data?.url || "";
};

const guessType = (r) => {
  const t = (r.tipo || r.tipo_contenido || "").toLowerCase();
  const mime = (r.mime || r.content_type || "").toLowerCase();
  const url = (r.url || r.enlace || r.link || "").toLowerCase();
  const ext = (r.extension || "").toLowerCase();

  if (t.includes("pdf") || mime.includes("pdf") || ext === "pdf" || url.endsWith(".pdf")) return "pdf";
  if (isPptMime(mime) || ext === "ppt" || ext === "pptx" || isPptExt(url) || t.includes("ppt")) return "ppt";
  if (
    t.includes("video") ||
    mime.startsWith("video") ||
    ["mp4", "avi", "mov", "wmv"].some((x) => url.endsWith(`.${x}`)) ||
    /youtube|vimeo/.test(url)
  )
    return "video";
  if (t.includes("link") || t.includes("enlace") || (!mime && url.startsWith("http"))) return "link";
  return "file";
};

const isPreviewable = (type) => ["pdf", "video"].includes(type);

/* ===================== Página ===================== */
export default function ModuloRecursos() {
  const { codigoCurso, moduloId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { getStructureResources, viewModuleResource, downloadModuleResource } = useCourseStructure();

  const [loading, setLoading] = useState(true);
  const [recursos, setRecursos] = useState([]);
  const [error, setError] = useState(null);

  // UI filtros
  const [q, setQ] = useState("");
  const [type, setType] = useState(""); // "", "pdf", "video", "link", "file"
  const [sort, setSort] = useState("recent"); // "recent" | "name"

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getStructureResources(moduloId);
        const list = data?.resources || data?.recursos || data?.structureResources || data || [];
        const visibles = Array.isArray(list) ? list.filter((r) => r.activo !== false) : [];
        if (!mounted) return;
        setRecursos(visibles);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "No se pudieron cargar los recursos.");
        setRecursos([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [moduloId, getStructureResources]);

  const filtered = useMemo(() => {
    const norm = (s = "") => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const nq = norm(q);
    let items = recursos.map((r) => ({ ...r, _type: guessType(r) }));

    if (q.trim()) {
      items = items.filter(
        (r) =>
          norm(pick(r, ["titulo", "title"], "")).includes(nq) ||
          norm(pick(r, ["descripcion", "description"], "")).includes(nq)
      );
    }
    if (type) items = items.filter((r) => r._type === type);

    if (sort === "name") {
      items = items.sort((a, b) =>
        pick(a, ["titulo", "title"], "").localeCompare(pick(b, ["titulo", "title"], ""), "es", {
          sensitivity: "base",
        })
      );
    } else {
      const dA = (x) => new Date(pick(x, ["fecha_subida", "fecha_creacion", "created_at"], 0));
      items = items.sort((a, b) => dA(b) - dA(a));
    }
    return items;
  }, [recursos, q, type, sort]);

  /* ===================== Acciones ===================== */
  const getSignedUrl = async (estructuraId, fileKey) => {
    try {
      const resp = await viewModuleResource(estructuraId, fileKey);
      return unwrapUrl(resp);
    } catch {
      return "";
    }
  };

  const downloadResource = async (r) => {
    const estructuraId = r.estructura_id || moduloId;
    const fileKey = pick(r, ["nombre_archivo", "file_name", "archivo", "key", "path"], "");
    const fileName = pick(r, ["nombre_original", "file_name", "nombre_archivo", "titulo", "title"], "recurso");

    if (!fileKey) {
      const rawUrl = pick(r, ["url", "enlace", "link"], "");
      if (rawUrl) {
        const a = document.createElement("a");
        a.href = rawUrl;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      alert("No se pudo descargar: falta el nombre del archivo.");
      return;
    }

    try {
      await downloadModuleResource(estructuraId, fileKey, fileName);
    } catch {
      const url = await getSignedUrl(estructuraId, fileKey);
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || fileKey || "recurso";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        alert("No se pudo descargar el archivo.");
      }
    }
  };

  const openResource = async (r) => {
    const t = r._type || guessType(r);
    const estructuraId = r.estructura_id || moduloId;

    // 🚫 No visualizar PPT/PPTX: solo descargar
    if (t === "ppt" || isPowerPoint(r)) {
      await downloadResource(r);
      return;
    }

    const display = pick(
      r,
      ["nombre_original", "titulo", "title", "nombre", "file_name", "nombre_archivo"],
      "recurso"
    );

    const from = location.pathname + location.search;

    const rawUrl = pick(r, ["url", "enlace", "link"], "");

    // Si el link apunta a PPT/PPTX, también forzar descarga
    if (t === "link" && rawUrl && isPptExt(rawUrl)) {
      await downloadResource(r);
      return;
    }

    if (t === "link" && rawUrl) {
      const safeURL = encodeURIComponent(rawUrl);
      const name = encodeURIComponent(display);
      navigate(
        `/visualizar?nombre=${name}&url=${safeURL}&curso=${codigoCurso}&modulo=${moduloId}&from=${encodeURIComponent(
          from
        )}`
      );
      return;
    }

    // Archivo interno (PDF/Video) con URL firmada
    const fileKey = pick(r, ["nombre_archivo", "file_name", "archivo", "key", "path"], "");
    if (isPreviewable(t) && fileKey) {
      const signed = await getSignedUrl(estructuraId, fileKey);
      if (signed) {
        const safeURL = encodeURIComponent(signed);
        const name = encodeURIComponent(display);
        navigate(
          `/visualizar?nombre=${name}&url=${safeURL}&curso=${codigoCurso}&modulo=${moduloId}&from=${encodeURIComponent(
            from
          )}`
        );
        return;
      }
    }

    // Fallback: URL pública
    if (rawUrl) {
      const safeURL = encodeURIComponent(rawUrl);
      const name = encodeURIComponent(display);
      navigate(
        `/visualizar?nombre=${name}&url=${safeURL}&curso=${codigoCurso}&modulo=${moduloId}&from=${encodeURIComponent(
          from
        )}`
      );
      return;
    }

    alert("No se pudo abrir el recurso (no hay URL disponible).");
  };

  const activeFilters = useMemo(() => {
    const chips = [];
    if (q.trim()) chips.push({ k: "q", label: `Buscar: “${q.trim()}”` });
    if (type) chips.push({ k: "type", label: `Tipo: ${type.toUpperCase()}` });
    if (sort === "name") chips.push({ k: "sort", label: "Orden: A→Z" });
    return chips;
  }, [q, type, sort]);

  const clearAll = () => {
    setQ("");
    setType("");
    setSort("recent");
  };

  /* ===================== UI ===================== */
  return (
    <div className="recursos-page">
      <div className="recursos-shell">
        {/* Header */}
        <div className="topbar">
          <button
            className="back"
            onClick={() => {
              if (location.state?.from) navigate(location.state.from);
              else navigate(`/curso/${codigoCurso}`);
            }}
          >
            <ArrowLeft size={18} /> Volver
          </button>

          <div className="titleBlock">
            <h1>
              <FolderOpen size={20} /> Recursos del módulo
            </h1>
            <p className="subtitle">
              {loading
                ? "Cargando…"
                : `${filtered.length} recurso${filtered.length === 1 ? "" : "s"} disponible${
                    filtered.length === 1 ? "" : "s"
                  }`}
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="filters">
          <div className="search">
            <input
              type="text"
              placeholder="🔎 Buscar por título o descripción…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {q && (
              <button className="clear" onClick={() => setQ("")} aria-label="Limpiar búsqueda" type="button">
                ✕
              </button>
            )}
          </div>

          <div className="selects">
            <label className="srOnly" htmlFor="typeSel">
              Tipo
            </label>
            <select id="typeSel" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Todos los tipos</option>
              <option value="pdf">PDF</option>
              <option value="video">Video</option>
              <option value="link">Enlace</option>
              <option value="file">Archivo</option>
            </select>

            <label className="srOnly" htmlFor="sortSel">
              Orden
            </label>
            <select id="sortSel" value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="recent">Más recientes</option>
              <option value="name">Por nombre (A→Z)</option>
            </select>

            <button className="ghostBtn" onClick={clearAll} type="button" title="Limpiar filtros">
              Limpiar
            </button>
          </div>

          {activeFilters.length > 0 && (
            <div className="chipsRow" aria-label="Filtros activos">
              {activeFilters.map((c) => (
                <span key={c.k} className="chip">
                  {c.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Panel */}
        <div className="panel">
          {loading ? (
            <div className="state muted">Cargando recursos…</div>
          ) : error ? (
            <div className="state error">⚠️ {error}</div>
          ) : filtered.length === 0 ? (
            <div className="state muted">No hay recursos que coincidan con tu búsqueda/filtros.</div>
          ) : (
            <div className="grid" role="list">
              {filtered.map((r, i) => {
                const t = r._type || guessType(r);
                const Icon = FILE_ICONS[t] || FolderOpen;
                const color = fileColor(t);
                const title = pick(r, ["titulo", "title", "nombre_original", "nombre_archivo", "file_name"], "Recurso");
                const desc = pick(r, ["descripcion", "description"], "");
                const fecha = pick(r, ["fecha_subida", "fecha_creacion", "created_at"], "");

                return (
                  <article key={r.id || i} className="card" role="listitem">
                    <div className="cardTop">
                      <div className={`type ${t}`} style={{ borderColor: color, color }}>
                        <Icon size={18} />
                        <span>{t.toUpperCase()}</span>
                      </div>
                    </div>

                    <div className="head">
                      <div className="icon" style={{ color, borderColor: color }}>
                        <Icon size={20} />
                      </div>
                      <h3 title={title}>{title}</h3>
                    </div>

                    {desc ? <p className="desc">{desc}</p> : <div className="desc placeholder">Sin descripción.</div>}

                    <div className="meta">
                      {pick(r, ["creado_por_nombre", "author"], "") ? (
                        <span>Por {pick(r, ["creado_por_nombre", "author"], "")}</span>
                      ) : null}
                      {fecha ? <span>{safeDate(fecha)}</span> : null}
                      {pick(r, ["tamano_bytes", "tamaño_archivo", "size"], "") ? (
                        <span>{fmtBytes(Number(pick(r, ["tamano_bytes", "tamaño_archivo", "size"], 0)))}</span>
                      ) : null}
                    </div>

                    <div className="actions">
                      {t !== "ppt" && (t === "link" || isPreviewable(t)) ? (
                        <button className="btn view" onClick={() => openResource(r)} type="button">
                          <Eye size={16} /> Ver
                        </button>
                      ) : null}

                      <button className="btn" onClick={() => downloadResource(r)} type="button">
                        <Download size={16} /> Descargar
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        :root{
          --radius: 14px;
          --border: #e5e7eb;
          --muted:#6b7280;
          --text:#1f2937;
          --brand:#0f5fb8;
          --panel:#ffffff;
          --shadow: 0 10px 30px rgba(0,0,0,.07);
          --shadow2: 0 6px 20px rgba(0,0,0,.06);
          --focus: #c7d2fe;
        }

        /* 🔒 Fix global de desbordes (inputs/selects/botones) */
        .recursos-page *{
          box-sizing: border-box;
          max-width: 100%;
          min-width: 0;
        }
        .recursos-page img, .recursos-page svg{ max-width: 100%; }

        .srOnly{
          position:absolute;
          width:1px;height:1px;
          padding:0;margin:-1px;
          overflow:hidden;clip:rect(0,0,0,0);
          white-space:nowrap;border:0;
        }

        .recursos-page{
          width:100%;
          padding: 12px;
        }

        .recursos-shell{
          width: clamp(320px, 96vw, 1200px);
          margin: 0 auto;
          color: var(--text);
        }

        /* Header */
        .topbar{
          display:flex;
          align-items:flex-start;
          gap: 12px;
          margin-bottom: 12px;
        }

        .back{
          border: 1px solid var(--border);
          background: #fff;
          padding: 10px 12px;
          border-radius: 12px;
          cursor: pointer;
          display:flex;
          align-items:center;
          gap: 8px;
          flex: 0 0 auto;
          white-space: nowrap;
        }

        .titleBlock{
          flex: 1 1 auto;
          min-width: 0;
        }
        .titleBlock h1{
          margin: 0;
          font-size: 1.15rem;
          color: var(--brand);
          display:flex;
          gap: 8px;
          align-items:center;
          line-height: 1.2;
          min-width: 0;
        }
        .subtitle{
          margin: 6px 0 0;
          color: var(--muted);
          font-size: .92rem;
        }

        /* Filters */
        .filters{
          background: linear-gradient(135deg,#eef5ff,#ffffff);
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 12px;
          margin-bottom: 12px;
          box-shadow: var(--shadow2);
          overflow: hidden; /* 🔒 nunca sobresale */
        }

        .search{
          position: relative;
          width: 100%;
        }

        .search input{
          width: 100%;
          display: block;
          padding: 12px 42px 12px 14px;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          outline: none;
          background: #fff;
          font-size: .95rem;
        }
        .search input:focus{
          border-color: var(--focus);
          box-shadow: inset 0 0 0 1px var(--focus);
        }

        .search .clear{
          position:absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          border: 0;
          background: rgba(0,0,0,.05);
          width: 32px;
          height: 32px;
          border-radius: 10px;
          cursor:pointer;
          color: var(--muted);
          display:flex;
          align-items:center;
          justify-content:center;
        }

        /* ✅ Grid de controles que NO rompe en móvil */
        .selects{
          margin-top: 10px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
          gap: 10px;
          align-items: center;
        }

        .selects select{
          width: 100%;
          display:block;
          padding: 12px 12px;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          outline: none;
          cursor: pointer;
          background: #fff;
          font-size: .95rem;
        }
        .selects select:focus{
          border-color: var(--focus);
          box-shadow: inset 0 0 0 1px var(--focus);
        }

        .ghostBtn{
          width: auto;
          border: 1px solid var(--border);
          background: rgba(0,0,0,.05);
          color: #111827;
          padding: 12px 12px;
          border-radius: 12px;
          cursor: pointer;
          font-weight: 900;
          white-space: nowrap;
        }

        .chipsRow{
          margin-top: 10px;
          display:flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .chip{
          display:inline-flex;
          align-items:center;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: .82rem;
          font-weight: 900;
          color: #111827;
          background: rgba(15,95,184,.08);
          border: 1px solid rgba(15,95,184,.18);
          max-width: 100%;
        }

        /* Panel */
        .panel{
          width: 100%;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 12px;
          box-shadow: var(--shadow);
          min-height: 520px;
          overflow: hidden; /* 🔒 nada se sale */
        }

        .state{
          padding: 28px;
          text-align: center;
          border: 1px dashed var(--border);
          border-radius: 14px;
          background: #fff;
        }
        .state.muted{ color: var(--muted); }
        .state.error{ color: #b91c1c; }

        .grid{
          display:grid;
          gap: 12px;
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        /* Card */
        .card{
          background:#fff;
          border:1px solid var(--border);
          border-radius: 16px;
          padding: 14px;
          display:flex;
          flex-direction:column;
          gap: 10px;
          box-shadow: var(--shadow2);
          transition: transform .15s ease, box-shadow .2s ease, border-color .2s ease;
          min-height: 210px;
          overflow: hidden; /* 🔒 textos/botones nunca sobresalen */
        }

        .cardTop{ display:flex; justify-content:flex-start; }

        .type{
          display:inline-flex;
          align-items:center;
          gap: 8px;
          font-size: .75rem;
          font-weight: 900;
          border: 1px dashed var(--border);
          padding: 4px 10px;
          border-radius: 999px;
          width: max-content;
          max-width: 100%;
        }

        .head{
          display:flex;
          align-items:center;
          gap: 10px;
          min-width: 0;
        }

        .icon{
          width: 38px;
          height: 38px;
          border-radius: 12px;
          background:#fff;
          display:flex;
          align-items:center;
          justify-content:center;
          border:1px solid var(--border);
          flex: 0 0 auto;
        }

        .head h3{
          margin: 0;
          font-size: 1rem;
          color:#111827;
          line-height: 1.2;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .desc{
          margin: 0;
          color: var(--muted);
          font-size: .92rem;
          line-height: 1.35;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .desc.placeholder{
          color: #9ca3af;
          font-style: italic;
        }

        .meta{
          display:flex;
          gap: 10px;
          flex-wrap: wrap;
          color: var(--muted);
          font-size: .82rem;
          overflow: hidden;
        }
        .meta span{
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .actions{
          display:flex;
          gap: 8px;
          margin-top: auto;
          flex-wrap: wrap;
        }

        .btn{
          background:#fff;
          color:#111827;
          border:1px solid var(--border);
          padding: 10px 12px;
          border-radius: 12px;
          cursor:pointer;
          font-weight: 900;
          display:inline-flex;
          gap: 6px;
          align-items:center;
          transition: transform .12s ease, box-shadow .2s ease, border-color .2s ease, background .2s ease;
          flex: 1 1 140px; /* ✅ evita botones gigantes o que rompan */
          justify-content: center;
          min-width: 0;
        }

        .btn.view{
          background:#eff6ff;
          color:#1e3a8a;
          border-color:#bfdbfe;
        }

        /* ======= Breakpoints ======= */
        @media (max-width: 1024px){
          .grid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }

        @media (max-width: 640px){
          .recursos-page{ padding: 10px; }

          .topbar{
            flex-direction: column;
            align-items: stretch;
          }

          .back{
            width: 100%;
            justify-content: center;
          }

          .filters{
            padding: 10px;
            border-radius: 12px;
          }

          /* ✅ En móvil, cada control en su fila y sin desbordes */
          .selects{
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .ghostBtn{
            width: 100%;
          }

          .panel{
            padding: 10px;
            border-radius: 14px;
            min-height: 440px;
          }

          .grid{
            grid-template-columns: 1fr;
          }

          .btn{
            flex: 1 1 100%;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}
